import { useState } from 'react'
import { blockApi, type BlockBundle, type BlockValidationIssue } from '@/services/block-api-service'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { useChatCardsCopy } from './chat-cards-copy'

// ─── Types ────────────────────────────────────────────────────

interface ValidationIssue {
  stage?: number
  level: string
  message: string
  rule?: string
  path?: string
}

interface BlockValidation {
  name: string
  score: number
  grade: string
  issues: ValidationIssue[]
  enrichedFeedback?: string
}

export interface ValidationResultData {
  blocks: BlockValidation[]
  averageScore?: number
  overallGrade?: string
}

// ─── Component ────────────────────────────────────────────────

const GRADE_COLORS: Record<string, string> = {
  pass: 'bg-muted text-foreground',
  warn: 'bg-muted text-muted-foreground',
  reject: 'bg-destructive/10 text-destructive',
  partial: 'bg-muted text-muted-foreground',
}

export function ValidationResultMessage({ data }: { data: ValidationResultData }) {
  const copy = useChatCardsCopy().checkResults
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null)
  const [fixingBlock, setFixingBlock] = useState<string | null>(null)
  const [fixResults, setFixResults] = useState<Record<string, { score: number; grade: string; source?: string }>>({})

  const avgScore =
    data.averageScore ??
    (data.blocks.length > 0
      ? Math.round(data.blocks.reduce((s, b) => s + b.score, 0) / data.blocks.length)
      : 0)
  const grade =
    data.overallGrade ?? (avgScore >= 80 ? 'pass' : avgScore >= 60 ? 'warn' : 'reject')

  async function handleFix(block: BlockValidation) {
    setFixingBlock(block.name)
    try {
      const project = useStudioStore.getState().currentProject
      if (!project?.blocks?.[block.name]) {
        toast.error(copy.blockNotFound(block.name))
        return
      }

      const blockData = JSON.parse(JSON.stringify(project.blocks[block.name]))

      // Reconstruct BlockBundle from v2 BlockDef
      const bundle: BlockBundle = {
        block: blockData.meta ?? { name: block.name },
        ui: blockData.ui ?? { root: {} },
        events: blockData.events ?? {},
        flow: blockData.flow ?? {},
        variables: blockData.state?.variables ?? {},
        datasource: blockData.data ?? {},
      }

      const errorIssues: BlockValidationIssue[] = block.issues
        .filter((i) => i.level === 'error')
        .map((i) => ({ stage: i.stage ?? 0, level: 'error' as const, message: i.message }))

      if (errorIssues.length === 0) {
        toast.info(copy.nothingToUpdate)
        return
      }

      const fixResult = await blockApi.fixBlock(bundle, errorIssues)

      if (fixResult.ok && fixResult.fixedBundle) {
        // Re-validate the fixed bundle directly — fixBlock already normalized
        // it to v1 shape, and a toV2Bundle hop here would discard state/data.
        const valResult = await blockApi.validateBlock(fixResult.fixedBundle)
        setFixResults((prev) => ({
          ...prev,
          [block.name]: { score: valResult.score, grade: valResult.grade, source: valResult.source },
        }))
        if (valResult.source === 'local-fallback') {
          toast.info(copy.updatedFallback(block.name))
        } else {
          toast.success(copy.updatedWithScore(block.name, valResult.score))
        }
      } else {
        toast.error(copy.updateFailedBecause(fixResult.error ?? copy.unknownError))
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : copy.updateFailed)
    } finally {
      setFixingBlock(null)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3 my-2" data-testid="validation-result">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{copy.title}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{avgScore}/100</span>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${GRADE_COLORS[grade] ?? GRADE_COLORS.reject}`}
          >
            {grade}
          </span>
        </div>
      </div>

      {data.blocks.map((block) => {
        const isExpanded = expandedBlock === block.name
        const fixResult = fixResults[block.name]
        const displayScore = fixResult?.score ?? block.score
        const displayGrade = fixResult?.grade ?? block.grade
        const errorCount = block.issues.filter((i) => i.level === 'error').length

        return (
          <div key={block.name} className="border rounded p-2">
            <button
              onClick={() => setExpandedBlock(isExpanded ? null : block.name)}
              className="w-full flex items-center justify-between text-left"
            >
              <span className="text-sm font-medium truncate">{block.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {fixResult?.source === 'local-fallback' ? '—' : `${displayScore}/100`}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${GRADE_COLORS[displayGrade] ?? GRADE_COLORS.reject}`}
                >
                  {displayGrade}
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="mt-2 space-y-1">
                {block.issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span
                      className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                        issue.level === 'error' ? 'bg-destructive' : 'bg-muted-foreground'
                      }`}
                    />
                    <span className="text-muted-foreground">{issue.message}</span>
                  </div>
                ))}
                {errorCount > 0 && !fixResult && (
                  <button
                    onClick={() => handleFix(block)}
                    disabled={fixingBlock === block.name}
                    className="mt-2 px-3 py-1 text-xs rounded border border-border bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
                    data-testid={`fix-${block.name}`}
                  >
                    {fixingBlock === block.name
                      ? copy.updating
                      : copy.updateIssues(errorCount)}
                  </button>
                )}
                {fixResult && (
                  <div
                    className={`mt-2 text-xs font-medium ${
                      fixResult.source === 'local-fallback' ? 'text-muted-foreground' : 'text-foreground'
                    }`}
                    data-testid={`fixed-${block.name}`}
                  >
                    {fixResult.source === 'local-fallback'
                      ? copy.fixedWithoutScore(fixResult.grade)
                      : copy.fixedWithScore(fixResult.score, fixResult.grade)}
                  </div>
                )}
                {block.enrichedFeedback && (
                  <details className="mt-2 text-xs text-muted-foreground">
                    <summary className="cursor-pointer font-medium text-foreground">
                      {copy.whyAndHowToFix}
                    </summary>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/40 p-2 leading-relaxed">
                      {block.enrichedFeedback}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
